import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Search, Download, Printer, FileText, MoreVertical, Pencil, Trash2, UserPlus, Calendar, FileSpreadsheet, Paperclip, Users } from "lucide-react";
import { listVisitors, deleteVisitor, exportCsvUrl, exportXlsxUrl, fileUrl } from "@/lib/api";
import { formatHariTanggalID } from "@/lib/format";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

export default function VisitorList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(new Set());

  const toggleOne = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === items.length ? new Set() : new Set(items.map(i => i.id)));
  };
  const openBulkPrint = () => {
    if (selected.size === 0) { toast.error("Pilih minimal 1 kunjungan"); return; }
    const ids = Array.from(selected).join(",");
    window.open(`/cetak/absensi-gabungan?ids=${ids}`, "_blank");
  };
  const openGroupPrint = (instansi, tanggal) => {
    window.open(`/cetak/absensi-gabungan?instansi=${encodeURIComponent(instansi)}&tanggal=${tanggal}`, "_blank");
  };

  const load = () => {
    setLoading(true);
    listVisitors({ search: search || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined })
      .then(setItems)
      .catch(() => toast.error("Gagal memuat data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [search, dateFrom, dateTo]);

  const totalTamu = useMemo(
    () => items.reduce((s, v) => s + (Number(v.jumlah_tamu) || 0) + (Number(v.jumlah_pendamping) || 0), 0),
    [items]
  );

  const handleDelete = async (id) => {
    try {
      await deleteVisitor(id);
      toast.success("Data dihapus");
      load();
    } catch { toast.error("Gagal menghapus"); }
  };

  return (
    <div className="space-y-6" data-testid="visitor-list-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#C59B27] font-semibold">Buku Tamu</div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1">Daftar Kunjungan Resmi</h1>
          <p className="text-slate-600 text-sm mt-1">
            Total <span className="font-semibold text-slate-900">{items.length}</span> kunjungan ·{" "}
            <span className="font-semibold text-slate-900">{totalTamu}</span> tamu & pendamping
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={openBulkPrint} variant="outline" disabled={selected.size === 0} data-testid="bulk-print-button">
            <Users className="w-4 h-4 mr-2" /> Cetak Gabungan ({selected.size})
          </Button>
          <Button asChild className="btn-primary" data-testid="list-add-visitor-button">
            <Link to="/admin/tamu/baru"><UserPlus className="w-4 h-4 mr-2" /> Tamu Baru</Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="list-export-menu">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <a href={exportXlsxUrl()} data-testid="list-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)</a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href={exportCsvUrl()} data-testid="list-export-csv"><FileText className="w-4 h-4 mr-2" /> CSV</a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cari nama tamu, instansi, perihal, atau nomor surat…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white border-slate-300 focus-visible:ring-[#0B2545]"
              data-testid="visitor-search-input"
            />
          </div>
          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="pl-10 bg-white border-slate-300"
              data-testid="visitor-filter-date-from"
            />
          </div>
          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="pl-10 bg-white border-slate-300"
              data-testid="visitor-filter-date-to"
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-[#0B2545]">
              <TableRow className="hover:bg-[#0B2545] border-0">
                <TableHead className="text-slate-200 w-10">
                  <Checkbox
                    checked={items.length > 0 && selected.size === items.length}
                    onCheckedChange={toggleAll}
                    className="border-slate-400 data-[state=checked]:bg-[#C59B27] data-[state=checked]:border-[#C59B27]"
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead className="text-slate-200 uppercase text-xs tracking-wider">Tanggal</TableHead>
                <TableHead className="text-slate-200 uppercase text-xs tracking-wider">Tamu</TableHead>
                <TableHead className="text-slate-200 uppercase text-xs tracking-wider">Instansi</TableHead>
                <TableHead className="text-slate-200 uppercase text-xs tracking-wider">Perihal</TableHead>
                <TableHead className="text-slate-200 uppercase text-xs tracking-wider text-center">Lampiran</TableHead>
                <TableHead className="text-slate-200 uppercase text-xs tracking-wider text-center">Peserta</TableHead>
                <TableHead className="text-slate-200 uppercase text-xs tracking-wider text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-14">
                    <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                    <div className="text-slate-700 font-semibold">Belum ada kunjungan tercatat</div>
                    <div className="text-slate-500 text-sm mt-1">Mulai dengan menambahkan tamu baru.</div>
                  </TableCell>
                </TableRow>
              ) : items.map((v) => (
                <TableRow key={v.id} className="hover:bg-slate-50" data-testid={`visitor-row-${v.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(v.id)}
                      onCheckedChange={() => toggleOne(v.id)}
                      className="data-[state=checked]:bg-[#0B2545] data-[state=checked]:border-[#0B2545]"
                      data-testid={`row-checkbox-${v.id}`}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="font-medium text-slate-900">{formatHariTanggalID(v.tanggal_kunjungan)}</div>
                    <div className="text-xs text-slate-500">{v.waktu_kunjungan} WIB</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold text-slate-900">{v.nama_tamu}</div>
                    <div className="text-xs text-slate-500">{v.jabatan_tamu}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-slate-800">{v.instansi_asal}</div>
                    <div className="text-xs text-slate-500">{v.daerah_asal}</div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="text-sm text-slate-700 line-clamp-2">{v.perihal_kunjungan}</div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex gap-1 justify-center">
                      {v.surat_kunjungan_file && (
                        <a href={fileUrl(v.surat_kunjungan_file)} target="_blank" rel="noreferrer"
                           title="Surat Kunjungan" className="text-[#134074] hover:text-[#0B2545]">
                          <Paperclip className="w-4 h-4" />
                        </a>
                      )}
                      {v.surat_tugas_file && (
                        <a href={fileUrl(v.surat_tugas_file)} target="_blank" rel="noreferrer"
                           title="Surat Tugas" className="text-[#C59B27] hover:text-[#8a6b1a]">
                          <Paperclip className="w-4 h-4" />
                        </a>
                      )}
                      {!v.surat_kunjungan_file && !v.surat_tugas_file && <span className="text-slate-300 text-xs">—</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-amber-100 text-[#8a6b1a] hover:bg-amber-100 font-semibold">
                      {(Number(v.jumlah_tamu) || 0) + (Number(v.jumlah_pendamping) || 0)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`row-actions-${v.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem asChild>
                          <Link to={`/cetak/absensi/${v.id}`} target="_blank" data-testid={`print-absensi-button-${v.id}`}>
                            <Printer className="w-4 h-4 mr-2" /> Cetak Absensi
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/cetak/surat-pernyataan/${v.id}`} target="_blank" data-testid={`print-surat-button-${v.id}`}>
                            <FileText className="w-4 h-4 mr-2" /> Cetak Surat Pernyataan
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => openGroupPrint(v.instansi_asal, v.tanggal_kunjungan)}
                                          data-testid={`group-print-${v.id}`}>
                          <Users className="w-4 h-4 mr-2" /> Grup Instansi+Tanggal
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/tamu/edit/${v.id}`}><Pencil className="w-4 h-4 mr-2" /> Edit</Link>
                        </DropdownMenuItem>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600 focus:text-red-700" data-testid={`delete-button-${v.id}`}>
                              <Trash2 className="w-4 h-4 mr-2" /> Hapus
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus data kunjungan?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Data kunjungan <b>{v.nama_tamu}</b> akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(v.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-${v.id}`}>
                                Hapus
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
